import { useEffect, useMemo } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Button, Dialog } from "@mui/material";

import PreviewDialogHeader, { previewHeaderButtonSx } from "./PreviewDialogHeader";

interface Props {
  open: boolean;
  blob: Blob | null;
  filename: string;
  title: string;
  onClose: () => void;
}

/**
 * Shared in-app PDF preview dialog (no browser tab).
 */
export default function PdfInlinePreviewDialog({
  open,
  blob,
  filename,
  title,
  onClose,
}: Props) {
  const blobUrl = useMemo(() => {
    if (!blob) return null;
    return URL.createObjectURL(blob);
  }, [blob]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "document.pdf";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose} aria-labelledby="inline-pdf-preview-title">
      <PreviewDialogHeader
        titleId="inline-pdf-preview-title"
        title={title}
        onClose={onClose}
        actions={
          <>
            <Button variant="contained" size="small" onClick={onClose} sx={previewHeaderButtonSx.secondary}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleDownload}
              disabled={!blobUrl}
              sx={previewHeaderButtonSx.primary}
            >
              Download PDF
            </Button>
          </>
        }
      />

      {blobUrl ? (
        <iframe
          src={blobUrl}
          title={filename || title}
          style={{ border: 0, width: "100%", height: "100%" }}
        />
      ) : null}
    </Dialog>
  );
}

